import { useId, useState, type ReactNode } from 'react';
import { OPERATOR_LABELS, operatorsFor, type ComparisonOperator, type ScopeVariable } from './condition-model';
import { appendAt, duplicateAt, fromExpression, GROUP_LABELS, MAX_GROUP_DEPTH, nodeExpression, replaceAt, sentence, toExpression, type ConditionGroup, type ConditionLeaf, type ConditionNode, type GroupMode, type NodePath } from './condition-tree';

/**
 * Constructeur de conditions à groupes imbriqués (ADR-146 addendum (3), maquette « Conditions ») : la règle
 * se lit en phrase, se construit par listes déroulantes, en groupes « Toutes », « Au moins une », « Aucune »
 * jusqu'à trois niveaux ; « Essayer » marque chaque ligne ✓ ou ✗ sur des valeurs d'exemple, par
 * l'évaluateur unique (jamais `eval`). Une expression hors de portée n'est jamais montrée en brut.
 */

export function ConditionBuilder({ label, value, onChange, variables, evaluate, emptyMeaning = 'Toujours' }: {
  label: string;
  value: unknown;
  onChange: (value: unknown) => void;
  variables: readonly ScopeVariable[];
  /** Évaluation d'essai, sur des valeurs indexées par référence. */
  evaluate?: (expression: unknown, values: Readonly<Record<string, unknown>>) => boolean;
  emptyMeaning?: string;
}) {
  const [sample, setSample] = useState<Record<string, unknown>>({});
  const [trying, setTrying] = useState(false);
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const tree = fromExpression(value);

  if (tree === null) {
    return (
      <fieldset className="ay-expr ay-conditions">
        <legend className="ay-field__label">{label}</legend>
        <p className="ay-text">Cette condition est trop complexe pour être modifiée ici (plus de trois niveaux, ou un calcul). Elle reste appliquée telle quelle ; une personne du rôle développeur peut la reprendre.</p>
        <button type="button" className="ay-button ay-button--secondary" onClick={() => onChange(undefined)}>Remplacer par une condition simple</button>
      </fieldset>
    );
  }

  const write = (next: ConditionGroup): void => onChange(toExpression(next));
  const first = variables[0];
  const freshLeaf = (): ConditionLeaf => ({ kind: 'comparison', ref: first?.ref ?? '', op: operatorsFor(first?.type ?? 'other')[0]!, ...(first?.type === 'boolean' ? { value: true } : first?.options?.[0] !== undefined ? { value: first.options[0].value } : {}) });
  const verdict = (node: ConditionNode): boolean | undefined => {
    if (!trying || evaluate === undefined) return undefined;
    const expression = nodeExpression(node);
    return expression === undefined ? undefined : evaluate(expression, sample);
  };
  const used = variables.filter((variable) => JSON.stringify(tree).includes(`"${variable.ref}"`));
  const whole = toExpression(tree);

  const renderGroup = (group: ConditionGroup, path: NodePath): ReactNode => {
    const key = path.join('.');
    const depth = path.length;
    const isFolded = folded.has(key);
    const result = verdict(group);
    return (
      <div className={`ay-conditions__group is-depth-${depth}`} role="group" aria-label={depth === 0 ? 'Groupe de départ' : `Groupe de niveau ${depth + 1}`}>
        <div className="ay-conditions__group-head">
          <button type="button" className="ay-editor__tool" aria-expanded={!isFolded} aria-label={isFolded ? 'Déplier le groupe' : 'Réduire le groupe'} onClick={() => setFolded((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; })}>{isFolded ? '▸' : '▾'}</button>
          <span role="radiogroup" aria-label="Sorte de groupe" className="ay-conditions__modes">
            {(['all', 'any', 'none'] as GroupMode[]).map((mode) => (
              <button key={mode} type="button" role="radio" aria-checked={group.mode === mode} onClick={() => write(replaceAt(tree, path, { ...group, mode }))}>{GROUP_LABELS[mode]}</button>
            ))}
          </span>
          {isFolded && <span className="ay-studio__muted">{group.items.length} élément(s) masqué(s)</span>}
          {result !== undefined && <span className={`ay-conditions__verdict is-${result ? 'true' : 'false'}`}>{result ? '✓ vrai' : '✗ faux'}</span>}
          {depth > 0 && (
            <>
              <button type="button" className="ay-editor__tool" onClick={() => write(duplicateAt(tree, path))}>Dupliquer</button>
              <button type="button" className="ay-editor__tool ay-editor__tool--danger" onClick={() => write(replaceAt(tree, path, undefined))}>Supprimer le groupe</button>
            </>
          )}
        </div>
        {!isFolded && (
          <>
            <ol className="ay-conditions__items">
              {group.items.map((item, index) => (
                <li key={index}>
                  {index > 0 && <span className="ay-conditions__join" aria-hidden="true">{group.mode === 'all' ? 'ET' : group.mode === 'any' ? 'OU' : 'NI'}</span>}
                  {item.kind === 'group' ? renderGroup(item, [...path, index]) : <LeafRow leaf={item} index={index} variables={variables} verdict={verdict(item)} onChange={(next) => write(replaceAt(tree, [...path, index], next))} onRemove={() => write(replaceAt(tree, [...path, index], undefined))} />}
                </li>
              ))}
            </ol>
            <div className="ay-conditions__add">
              <button type="button" className="ay-button ay-button--secondary" disabled={variables.length === 0} onClick={() => write(appendAt(tree, path, freshLeaf()))}>+ Condition</button>
              {depth + 1 < MAX_GROUP_DEPTH ? (
                <button type="button" className="ay-button ay-button--secondary" disabled={variables.length === 0} onClick={() => write(appendAt(tree, path, { kind: 'group', mode: 'any', items: [freshLeaf()] }))}>+ Groupe</button>
              ) : (
                <span className="ay-studio__muted">Trois niveaux au plus. Pour aller plus loin, découpez en deux règles.</span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <fieldset className="ay-expr ay-conditions">
      <legend className="ay-field__label">{label}</legend>
      <p className="ay-conditions__sentence" role="status">
        {tree.items.length === 0 ? `${emptyMeaning} (aucune condition).` : `${label} : ${sentence(tree, variables)}.`}
      </p>
      {renderGroup(tree, [])}
      {evaluate !== undefined && tree.items.length > 0 && (
        <div className="ay-expr__test">
          <button type="button" className="ay-button ay-button--secondary" aria-expanded={trying} onClick={() => setTrying((value) => !value)}>Essayer sur un exemple</button>
          {trying && (
            <>
              {used.map((variable) => (
                <label key={variable.ref} className="ay-expr__sample">
                  <span>{variable.label}</span>
                  <ValueInput variable={variable} value={sample[variable.ref]} onChange={(next) => setSample((current) => ({ ...current, [variable.ref]: next }))} label={`Exemple : ${variable.label}`} />
                </label>
              ))}
              <p className="ay-expr__result" aria-live="polite">Pour cet exemple : <strong>{whole !== undefined && evaluate(whole, sample) ? 'vrai' : 'faux'}</strong></p>
            </>
          )}
        </div>
      )}
    </fieldset>
  );
}

function LeafRow({ leaf, index, variables, verdict, onChange, onRemove }: { leaf: ConditionLeaf; index: number; variables: readonly ScopeVariable[]; verdict: boolean | undefined; onChange: (next: ConditionLeaf) => void; onRemove: () => void }) {
  const variable = variables.find((candidate) => candidate.ref === leaf.ref);
  const operators = operatorsFor(variable?.type ?? 'other');
  const name = `Condition ${index + 1}`;
  return (
    <span className="ay-expr__row">
      <select aria-label={`${name} : donnée`} value={leaf.ref} onChange={(event) => {
        const next = variables.find((candidate) => candidate.ref === event.target.value);
        onChange({ kind: 'comparison', ref: event.target.value, op: operatorsFor(next?.type ?? 'other')[0]!, ...(next?.type === 'boolean' ? { value: true } : next?.options?.[0] !== undefined ? { value: next.options[0].value } : {}) });
      }}>
        {variable === undefined && <option value={leaf.ref}>Donnée qui n’existe plus</option>}
        {variables.map((candidate) => <option key={candidate.ref} value={candidate.ref}>{candidate.label}</option>)}
      </select>
      <select aria-label={`${name} : comparaison`} value={leaf.op} onChange={(event) => onChange({ ...leaf, op: event.target.value as ComparisonOperator })}>
        {operators.map((op) => <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>)}
      </select>
      {leaf.op !== 'filled' && leaf.op !== 'empty' && (leaf.op === 'in' || leaf.op === 'nin' ? <Choices leaf={leaf} variable={variable} label={`${name} : valeurs`} onChange={onChange} /> : <ValueInput variable={variable} value={leaf.value} onChange={(next) => onChange({ ...leaf, value: next })} label={`${name} : valeur`} />)}
      {verdict !== undefined && <span className={`ay-conditions__verdict is-${verdict ? 'true' : 'false'}`} title={verdict ? 'vraie pour l’exemple' : 'fausse pour l’exemple'}>{verdict ? '✓' : '✗'}</span>}
      <button type="button" className="ay-editor__tool" aria-label={`Supprimer la ${name.toLowerCase()}`} onClick={onRemove}>×</button>
    </span>
  );
}

function Choices({ leaf, variable, label, onChange }: { leaf: ConditionLeaf; variable: ScopeVariable | undefined; label: string; onChange: (next: ConditionLeaf) => void }) {
  const selected = new Set((Array.isArray(leaf.value) ? leaf.value : []).map(String));
  return (
    <span className="ay-expr__choices" role="group" aria-label={label}>
      {(variable?.options ?? []).map((option) => (
        <label key={option.value} className="ay-expr__choice">
          <input type="checkbox" checked={selected.has(option.value)} onChange={(event) => onChange({ ...leaf, value: (variable?.options ?? []).filter((candidate) => (candidate.value === option.value ? event.target.checked : selected.has(candidate.value))).map((candidate) => candidate.value) })} />
          {option.label}
        </label>
      ))}
    </span>
  );
}

function ValueInput({ variable, value, onChange, label }: { variable: ScopeVariable | undefined; value: unknown; onChange: (value: unknown) => void; label: string }) {
  const id = useId();
  if (variable?.type === 'boolean') {
    return (
      <select id={id} aria-label={label} value={value === true ? 'true' : value === false ? 'false' : ''} onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value === 'true')}>
        <option value="">—</option>
        <option value="true">oui</option>
        <option value="false">non</option>
      </select>
    );
  }
  if (variable?.type === 'enum' && variable.options !== undefined) {
    return (
      <select id={id} aria-label={label} value={value === undefined || value === null ? '' : String(value)} onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}>
        <option value="">—</option>
        {variable.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    );
  }
  if (variable?.type === 'number') return <input id={id} aria-label={label} type="number" inputMode="decimal" value={typeof value === 'number' ? value : ''} onChange={(event) => onChange(event.target.value === '' ? undefined : Number(event.target.value))} />;
  return <input id={id} aria-label={label} type={variable?.type === 'date' ? 'date' : 'text'} value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)} />;
}
